import { Check, Copy, Download, ExternalLink, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface RequestReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The salon's Google `placeId` from `GoogleBusinessConnection.placeId`.
   * When `null` the dialog should never be opened — the parent card hides
   * the CTA in that case. We still defensive-render a hint here in case
   * something gets out of sync.
   */
  placeId: string | null;
  /** Used as the QR code's download filename. */
  locationName: string;
}

/** Google's direct "write a review" form for a given Place. The
 *  `placeid=` parameter is the modern Place ID; this URL has been stable
 *  for years and matches what the Google Business dashboard offers as
 *  "Share review form" under "Get more reviews". */
function buildReviewUrl(placeId: string): string {
  return `https://search.google.com/local/writereview?placeid=${encodeURIComponent(placeId)}`;
}

const QR_PIXEL_SIZE = 220;
// Slightly stronger than default ("M") so the QR survives bad printing,
// dim lighting, and curved/glossy surfaces (typical at a salon reception).
const QR_ERROR_LEVEL = "Q" as const;

export function RequestReviewDialog({
  open,
  onOpenChange,
  placeId,
  locationName,
}: RequestReviewDialogProps) {
  const { t } = useTranslation("reviews");
  const [copied, setCopied] = useState(false);

  if (!placeId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("requestReview.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("requestReview.noPlaceId")}</DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  const url = buildReviewUrl(placeId);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t("requestReview.linkCopied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(t("requestReview.copyFailed"));
    }
  };

  const handleDownloadQr = () => {
    // qrcode.react renders an <svg id=> we can serialize directly; no
    // canvas roundtrip needed. SVG download is sharp at any print size,
    // which matches the "print and put at reception" use case.
    const svg = document.getElementById("request-review-qr") as SVGElement | null;
    if (!svg) return;
    const serializer = new XMLSerializer();
    const blob = new Blob([serializer.serializeToString(svg)], { type: "image/svg+xml" });
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = `${locationName.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "salon"}-review-qr.svg`;
    link.click();
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5" />
            {t("requestReview.dialogTitle")}
          </DialogTitle>
          <DialogDescription>{t("requestReview.dialogDescription")}</DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          {/* QR — white background even in dark mode so the contrast stays
              reliable when scanned by a phone camera under salon lighting. */}
          <div className="flex justify-center">
            <div className="rounded-lg border bg-white p-4">
              <QRCodeSVG
                id="request-review-qr"
                value={url}
                size={QR_PIXEL_SIZE}
                level={QR_ERROR_LEVEL}
                includeMargin={false}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs break-all">
              {url}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              aria-label={t("requestReview.copy")}
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button type="button" variant="outline" onClick={handleDownloadQr} className="flex-1">
              <Download className="mr-2 h-4 w-4" />
              {t("requestReview.downloadQr")}
            </Button>
            <Button type="button" variant="outline" asChild className="flex-1">
              <a href={url} target="_blank" rel="noreferrer noopener">
                <ExternalLink className="mr-2 h-4 w-4" />
                {t("requestReview.preview")}
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
